import { STORAGE_KEYS } from './constants';
import { readJSON, readString, remove, writeJSON, writeString } from './storage';

declare const __APP_BASE_PATH__: string | undefined;
declare const __PREVIEW_PR_NUMBER__: string | null | undefined;

declare global {
  interface Window {
    __talkynPreviewBootTimer?: number;
  }
}

const DEFAULT_STABLE_APP_BASE_PATH = '/app/';
const PREVIEW_PENDING_KEY = 'talkyn-preview-pending';
const PREVIEW_FAILURE_KEY = 'talkyn-preview-failure';

export interface PendingPreviewActivation {
  prNumber: number;
  targetPath: string;
  stableUrl: string;
  startedAt: number;
}

export interface PreviewActivationFailure {
  prNumber: number;
  failedAt: string;
  reason: string;
  stableUrl: string;
}

function normalizePath(pathname: string): string {
  const collapsed = pathname.replace(/\/+/g, '/');
  if (!collapsed || collapsed === '/') return '/';
  return collapsed.endsWith('/') ? collapsed : `${collapsed}/`;
}

export function normalizeBasePath(pathname: string): string {
  const normalized = normalizePath(pathname.trim() || '/');
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

function parsePreviewPrNumber(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const match = raw.match(/(\d+)/u);
  if (!match) return null;
  const prNumber = Number.parseInt(match[1] ?? '', 10);
  return Number.isFinite(prNumber) && prNumber > 0 ? prNumber : null;
}

function normalizePreviewNumber(raw: string | number): string | null {
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  if (!/^\d+$/u.test(trimmed)) return null;
  const prNumber = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(prNumber) || prNumber <= 0) return null;
  return String(prNumber);
}

export function getPreviewPrNumberFromPath(pathname: string): number | null {
  const match = normalizePath(pathname).match(/\/app\/pr-(\d+)\/$/u);
  return match?.[1] ? parsePreviewPrNumber(match[1]) : null;
}

function deriveBasePathFromLocation(pathname: string): string {
  const normalizedPath = normalizePath(pathname);
  const previewMatch = normalizedPath.match(/^(.*?\/app\/pr-\d+\/)/u);
  if (previewMatch?.[1]) return normalizeBasePath(previewMatch[1]);

  const stableMatch = normalizedPath.match(/^(.*?\/app\/)/u);
  if (stableMatch?.[1]) return normalizeBasePath(stableMatch[1]);

  if (!pathname || pathname === '/') return '/';

  const lastSlash = pathname.lastIndexOf('/');
  if (lastSlash <= 0) return '/';
  return normalizeBasePath(pathname.slice(0, lastSlash + 1));
}

export function isExpectedPreviewBuildVersion(prNumber: string | number, version: string): boolean {
  const normalizedPreviewNumber = normalizePreviewNumber(prNumber);
  if (!normalizedPreviewNumber) return false;

  return new RegExp(`^pr-${normalizedPreviewNumber}-[0-9a-f]{7,}$`, 'iu').test(version.trim());
}

export function getAppBasePath(): string {
  if (typeof __APP_BASE_PATH__ === 'string' && __APP_BASE_PATH__.trim()) {
    return normalizeBasePath(__APP_BASE_PATH__);
  }

  if (typeof window !== 'undefined') {
    return deriveBasePathFromLocation(window.location.pathname);
  }

  return DEFAULT_STABLE_APP_BASE_PATH;
}

export function getStableBasePath(): string {
  const currentBasePath = getAppBasePath();
  if (/\/app\/pr-\d+\/$/u.test(currentBasePath)) {
    return DEFAULT_STABLE_APP_BASE_PATH;
  }
  if (currentBasePath === '/' || currentBasePath === DEFAULT_STABLE_APP_BASE_PATH) {
    return currentBasePath;
  }
  if (currentBasePath.includes('/app/')) {
    return DEFAULT_STABLE_APP_BASE_PATH;
  }
  return currentBasePath;
}

export function getCurrentPreviewPrNumber(): number | null {
  const definedPreviewValue = typeof __PREVIEW_PR_NUMBER__ !== 'undefined'
    ? __PREVIEW_PR_NUMBER__
    : null;
  return parsePreviewPrNumber(definedPreviewValue);
}

export function getSelectedPreviewPrNumber(): number | null {
  return parsePreviewPrNumber(readString(STORAGE_KEYS.PREVIEW_PR_NUMBER, ''));
}

export function setSelectedPreviewPr(prNumber: string): void {
  const normalizedPreviewNumber = normalizePreviewNumber(prNumber);
  if (!normalizedPreviewNumber) {
    clearSelectedPreviewPr();
    return;
  }

  writeString(STORAGE_KEYS.PREVIEW_PR_NUMBER, normalizedPreviewNumber);
}

export function clearSelectedPreviewPr(): void {
  remove(STORAGE_KEYS.PREVIEW_PR_NUMBER);
}

export function isPreviewBuild(): boolean {
  return getCurrentPreviewPrNumber() !== null;
}

export function buildPreviewBasePath(prNumber: string | number): string {
  const normalizedPreviewNumber = normalizePreviewNumber(prNumber);
  if (!normalizedPreviewNumber) {
    throw new Error('Preview PR number must be a positive integer');
  }

  const stableBasePath = getStableBasePath().replace(/\/$/u, '');
  return normalizeBasePath(`${stableBasePath}/pr-${normalizedPreviewNumber}/`);
}

export function buildPreviewUrl(prNumber: string | number, currentHref: string = window.location.href): string {
  const url = new URL(currentHref);
  url.pathname = buildPreviewBasePath(prNumber);
  return url.toString();
}

export function buildStableUrl(currentHref: string = window.location.href): string {
  const url = new URL(currentHref);
  url.pathname = getStableBasePath();
  return url.toString();
}

export function getRequestedPreviewPr(): string {
  const previewFallback = getSelectedPreviewPrNumber() ?? getCurrentPreviewPrNumber();
  return readString(
    STORAGE_KEYS.PREVIEW_PR_DRAFT,
    previewFallback ? String(previewFallback) : '',
  );
}

export function setRequestedPreviewPr(prNumber: string): void {
  const normalizedPreviewNumber = normalizePreviewNumber(prNumber);
  if (!normalizedPreviewNumber) {
    clearRequestedPreviewPr();
    return;
  }
  writeString(STORAGE_KEYS.PREVIEW_PR_DRAFT, normalizedPreviewNumber);
}

export function clearRequestedPreviewPr(): void {
  remove(STORAGE_KEYS.PREVIEW_PR_DRAFT);
}

export async function fetchPreviewBuildVersion(prNumber: string | number): Promise<string | null> {
  const versionUrl = new URL(`${buildPreviewBasePath(prNumber)}version.txt`, window.location.origin);

  try {
    const response = await fetch(versionUrl.toString(), {
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    });
    if (!response.ok) return null;

    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
    if (!contentType.includes('text/plain')) return null;

    const version = (await response.text()).trim();
    return isExpectedPreviewBuildVersion(prNumber, version) ? version : null;
  } catch {
    return null;
  }
}

export function beginPreviewActivation(prNumber: number): string {
  const targetUrl = buildPreviewUrl(prNumber);
  const targetPath = normalizeBasePath(new URL(targetUrl).pathname);
  const stableUrl = buildStableUrl();

  clearPreviewActivationFailure();
  setRequestedPreviewPr(String(prNumber));
  setSelectedPreviewPr(String(prNumber));
  writeJSON<PendingPreviewActivation>(PREVIEW_PENDING_KEY, {
    prNumber,
    targetPath,
    stableUrl,
    startedAt: Date.now(),
  });

  return targetUrl;
}

export function clearPendingPreviewActivation(): void {
  remove(PREVIEW_PENDING_KEY);
}

export function getPendingPreviewActivation(): PendingPreviewActivation | null {
  return readJSON<PendingPreviewActivation | null>(PREVIEW_PENDING_KEY, null);
}

export function markPreviewBootSuccess(): void {
  const selectedPreviewPr = getSelectedPreviewPrNumber();
  const runningPreviewPr = getCurrentPreviewPrNumber();
  const previewPathPr = getPreviewPrNumberFromPath(window.location.pathname);

  if (!selectedPreviewPr || previewPathPr !== selectedPreviewPr || runningPreviewPr !== selectedPreviewPr) {
    return;
  }

  if (window.__talkynPreviewBootTimer) {
    window.clearTimeout(window.__talkynPreviewBootTimer);
    delete window.__talkynPreviewBootTimer;
  }

  clearPendingPreviewActivation();
}

export function getPreviewActivationFailure(): PreviewActivationFailure | null {
  return readJSON<PreviewActivationFailure | null>(PREVIEW_FAILURE_KEY, null);
}

export function clearPreviewActivationFailure(): void {
  remove(PREVIEW_FAILURE_KEY);
}

export function recordPreviewActivationFailure(failure: PreviewActivationFailure): void {
  writeJSON(PREVIEW_FAILURE_KEY, failure);
}
